import { MixDetailsSummary } from "./MixDetailsSummary";
import { useTenantTranslation } from "hooks/formatters";
import { MixDetailsPropertiesTab } from "./MixDetailsPropertiesTab";
import { LayeringTab } from "./LayeringTab";
import { TabSpec, TitledTabs } from "src/components/common/titledTabs";
import { ChemistryGroupId, SteelGrade } from "contexts/search/context";
import {
  OptimisationResultSetSummary,
  ChemicalElement,
  ExternalRecipeWithSummary,
  MixBasketSummary,
  MixMaterialBasketSummary,
  BasketTabType,
  ChemicalConstraint,
  MaterialRead,
} from "src/store/api/generatedApi";
import { BasketsTab } from "./BasketsTab";
import { ImpuritiesTab } from "./ImpuritiesTab";
import { InventoryDepletionTab } from "./InventoryDepletionTab";
import { RecipeComparisonTab } from "./RecipeComparisonTab";
import { PlanId } from "components/common/boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { useTenantData } from "contexts/tenant";
import MixDetailsFormattedName from "./MixDetailsFormattedName";
import NoMixSummaryForGroupErrorMessage from "./NoMixSummaryForGroupErrorMessage";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";

export interface MixDetailsProps {
  chemistryGroupId: ChemistryGroupId;
  chemistryGroupName: string;
  steelGrades: SteelGrade[];
  maximumFailureRate: number;
  defaultTargetNumberOfBaskets: number;
  mix: number;
  summary: OptimisationResultSetSummary;
  elements: Record<number, ChemicalElement>;
  planId: PlanId;
  period: Period;
  multipleMixes: boolean;
  chemicalConstraints: ChemicalConstraint[];
  materials: MaterialRead[];
  externalRecipes: Record<string, ExternalRecipeWithSummary> | null;
}

interface BasketTabProps {
  basketTabType: BasketTabType;
  planId: PlanId;
  chemistryGroupId: number;
  mix: number;
  summary: OptimisationResultSetSummary;
  name: string;
  period: Period;
  materials: MaterialRead[];
}

type TabKey =
  | "baskets"
  | "impurities"
  | "similarChargedRecipes"
  | "properties"
  | "inventoryDepletion"
  | "recipeComparison";

const filterForChefGroup =
  (chefGroupName: string, period: Period, mix?: number) =>
  (item: MixBasketSummary | MixMaterialBasketSummary) => {
    return (
      item.chef_group_name === chefGroupName &&
      period === item.period &&
      (mix === undefined || item.mix_number === mix)
    );
  };

const BasketTab = ({
  basketTabType,
  planId,
  chemistryGroupId,
  mix,
  summary,
  name,
  period,
  materials,
}: BasketTabProps) => {
  switch (basketTabType) {
    case "layering_table":
      return (
        <LayeringTab
          planId={planId}
          groupId={chemistryGroupId}
          mixNumber={mix}
          period={period}
        />
      );
    case "basket_plot":
      return (
        <BasketsTab
          mixBasketsSummary={summary.mix_basket_summary.filter(
            filterForChefGroup(name, period, mix)
          )}
          mixMaterialBasketsSummary={summary.mix_material_basket_summary.filter(
            filterForChefGroup(name, period, mix)
          )}
          materials={materials}
        />
      );
  }
};

export const MixDetailsView = ({
  chemistryGroupId,
  chemistryGroupName,
  steelGrades,
  mix,
  summary,
  elements,
  planId,
  multipleMixes,
  period,
  maximumFailureRate,
  defaultTargetNumberOfBaskets,
  chemicalConstraints,
  materials,
  externalRecipes,
}: MixDetailsProps) => {
  const { t } = useTenantTranslation();
  const { basket_tab_type: basketTabType } = useTenantData();
  const isMobile = useIsMobile();
  const showInventoryDepletion = useIsBehindFeatureFlag(
    "mix_inventory_depletion"
  );
  const showRecipeComparison = useIsBehindFeatureFlag(
    "external_recipe_comparison"
  );
  const [selectedTabKey, setSelectedTabKey] = useStringQueryParamState<TabKey>(
    "mix_details_tab_selected",
    "baskets"
  );
  const thisGroupSummary = summary.mix_summary.find(
    (mix_summary) =>
      mix_summary.chef_group_id === chemistryGroupId &&
      mix_summary.period === period
  );
  if (thisGroupSummary === undefined) {
    return (
      <NoMixSummaryForGroupErrorMessage
        chemistryGroupId={chemistryGroupId}
        chemistryGroupName={chemistryGroupName}
      />
    );
  }

  const tabSpecs: TabSpec<TabKey>[] = [
    {
      title: t("baskets"),
      key: "baskets",
      content: (
        <BasketTab
          basketTabType={basketTabType}
          planId={planId}
          chemistryGroupId={chemistryGroupId}
          mix={mix}
          summary={summary}
          name={chemistryGroupName}
          period={period}
          materials={materials}
        />
      ),
    },
    {
      title: t("impurities"),
      key: "impurities",
      content: (
        <ImpuritiesTab
          planId={planId}
          mixId={thisGroupSummary.mix_id}
          period={period}
        />
      ),
    },
    {
      title: t("properties"),
      key: "properties",
      content: (
        <MixDetailsPropertiesTab
          maximumFailureRate={maximumFailureRate}
          defaultTargetNumberOfBaskets={defaultTargetNumberOfBaskets}
          chemicalConstraints={chemicalConstraints}
          steelGrades={steelGrades}
          elements={elements}
          chefGroupId={chemistryGroupId}
        />
      ),
    },
    ...(showInventoryDepletion
      ? [
          {
            title: t("inventoryDepletion"),
            key: "inventoryDepletion" as const,
            content: (
              <InventoryDepletionTab
                mixMaterialSummary={summary.mix_material_summary}
                chefGroupName={chemistryGroupName}
                period={period}
                mixNumber={mix}
              />
            ),
          },
        ]
      : []),
    ...(showRecipeComparison && externalRecipes?.[chemistryGroupName]
      ? [
          {
            title: t("recipeComparison"),
            key: "recipeComparison" as const,
            content: (
              <RecipeComparisonTab
                chemistryGroupName={chemistryGroupName}
                period={period}
                mixNumber={mix}
                mixMaterialSummary={summary.mix_material_summary}
                mixMaterialBasketSummary={summary.mix_material_basket_summary}
                mixSummary={thisGroupSummary}
                externalRecipe={externalRecipes[chemistryGroupName]}
                materials={materials}
              />
            ),
          },
        ]
      : []),
  ];

  if (isMobile) {
    return (
      <BasketTab
        basketTabType={basketTabType}
        planId={planId}
        chemistryGroupId={chemistryGroupId}
        mix={mix}
        summary={summary}
        name={chemistryGroupName}
        period={period}
        materials={materials}
      />
    );
  }

  return (
    <>
      <MixDetailsFormattedName
        chemistryGroupName={chemistryGroupName}
        mix={mix}
        multipleMixes={multipleMixes}
      />
      <MixDetailsSummary
        mixSummary={summary.mix_summary}
        chefGroupName={chemistryGroupName}
        period={period}
      />

      <TitledTabs
        selectedTabKey={selectedTabKey}
        onChange={(key) => {
          setSelectedTabKey(key);
        }}
        tabSpecs={tabSpecs}
      />
    </>
  );
};
